import { useGLTF, Environment } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import React, { Suspense, useEffect, useState } from 'react';
import * as THREE from 'three';

/**
 * StonePhone3D - Renders the 3D Stone phone model
 * Positioned behind text in Frame 1
 */

interface PhoneModelProps {
  modelPath: string;
  rotationProgress: number;
  scaleMultiplier?: number;
  cursorX?: number; // Normalized 0-1 (0.5 = center)
  cursorY?: number; // Normalized 0-1 (0.5 = center)
  cursorRotationSettings?: {
    enabled: boolean;
    sensitivity: number;
    maxRotation: number;
  };
  isMobile?: boolean;
  autoRotationAngle?: number; // For mobile auto-rotation
  noShine?: boolean; // Remove metallic shine for UI showcase
  demoProgress?: number; // Progress for LANDING 2 → DEMO transition (tilted → front-facing)
}

function PhoneModel({
  modelPath,
  rotationProgress,
  scaleMultiplier = 1,
  cursorX = 0.5,
  cursorY = 0.5,
  cursorRotationSettings = { enabled: false, sensitivity: 0, maxRotation: 0 },
  isMobile = false,
  autoRotationAngle = 0,
  noShine = false,
  demoProgress = 0
}: PhoneModelProps) {
  const gltf = useGLTF(modelPath);
  const [isReady, setIsReady] = useState(false);

  // Store frozen cursor offset when transition starts
  const frozenOffsetRef = React.useRef<{ x: number; y: number; z: number } | null>(null);
  const prevProgressRef = React.useRef(0);

  useEffect(() => {
    console.log('✅ 3D Model loaded:', gltf);
    if (gltf.scene) {
      console.log('✅ Scene exists with', gltf.scene.children.length, 'children');

      // Force center the model by calculating its bounding box
      const box = new THREE.Box3().setFromObject(gltf.scene);
      const center = box.getCenter(new THREE.Vector3());

      // Offset the model to truly center it
      gltf.scene.position.set(-center.x, -center.y, -center.z);

      console.log('📐 Model center offset:', center);

      // Make all meshes black
      gltf.scene.traverse((child: any) => {
        if (child.isMesh) {
          console.log('✅ Found mesh:', child.name);
          // Override material to dark gray with perfectly smooth shading
          child.material = new THREE.MeshStandardMaterial({
            color: noShine ? '#3a3a3a' : '#242424', // Lighter gray for visibility
            // color: '#adadad',
            roughness: noShine ? 0.9 : 0,
            metalness: noShine ? 0.1 : 1.0,
            envMapIntensity: noShine ? 0.2 : 2.0
          });
          // Enable smooth shading for better edge rendering
          child.material.flatShading = false;
          child.material.needsUpdate = true;
          // Ensure geometry normals are computed for smooth shading
          if (child.geometry) {
            child.geometry.computeVertexNormals();
            // Increase geometry smoothness
            if (!child.geometry.attributes.normal) {
              child.geometry.computeVertexNormals();
            }
          }
        }
      });

      // Mark as ready after model is fully loaded and centered
      setTimeout(() => setIsReady(true), 100);
    }
  }, [gltf]);

  // Don't render until model is fully loaded to prevent position jump
  if (!isReady) return null;

  // Calculate cursor-based rotation offset or auto-rotation for mobile
  let cursorOffsetX = 0;
  let cursorOffsetY = 0;
  let cursorOffsetZ = 0;

  if (isMobile && rotationProgress === 0) {
    // Mobile auto-rotation: full 360° rotation around Y axis
    const maxRotationRad = (360 * Math.PI) / 180; // 360 degrees for full rotation
    cursorOffsetY = autoRotationAngle * maxRotationRad;
    // Also add slight X and Z variation for more dynamic rotation
    cursorOffsetX = Math.sin(autoRotationAngle * Math.PI * 2) * 0.3;
    cursorOffsetZ = Math.cos(autoRotationAngle * Math.PI * 2) * 0.3;
  } else if (cursorRotationSettings.enabled && !isMobile) {
    // Desktop: cursor-based rotation relative to default pose
    // Convert normalized cursor position (0-1) to offset from center (-0.5 to 0.5)
    const offsetX = (cursorX - 0.5); // Positive = right, Negative = left
    const offsetY = (cursorY - 0.5); // Positive = bottom, Negative = top

    // Apply sensitivity and clamp to max rotation
    const { sensitivity, maxRotation } = cursorRotationSettings;
    const maxRotationRad = (maxRotation * Math.PI) / 180; // Convert degrees to radians

    // Simple two-axis rotation relative to default pose:
    // X-axis rotation: cursor moves up = tilt back (decrease X), cursor moves down = tilt forward (increase X)
    const currentOffsetX = Math.max(-maxRotationRad, Math.min(maxRotationRad, offsetY * sensitivity));

    // Z-axis rotation: cursor moves right = rotate right, cursor moves left = rotate left (inverted)
    const currentOffsetZ = Math.max(-maxRotationRad, Math.min(maxRotationRad, -offsetX * sensitivity));

    // No Y-axis rotation from cursor movement
    const currentOffsetY = 0;

    // If transition just started (progress went from 0 to non-zero), freeze the cursor offset
    if (rotationProgress > 0 && prevProgressRef.current === 0) {
      frozenOffsetRef.current = { x: currentOffsetX, y: currentOffsetY, z: currentOffsetZ };
    }

    // If transition is happening (progress > 0), use frozen offset
    // Otherwise use live cursor offset
    if (rotationProgress > 0 && frozenOffsetRef.current) {
      cursorOffsetX = frozenOffsetRef.current.x;
      cursorOffsetY = frozenOffsetRef.current.y;
      cursorOffsetZ = frozenOffsetRef.current.z;
    } else {
      cursorOffsetX = currentOffsetX;
      cursorOffsetY = currentOffsetY;
      cursorOffsetZ = currentOffsetZ;
    }

    // Reset frozen offset when back to Frame 1 (progress = 0)
    if (rotationProgress === 0 && prevProgressRef.current > 0) {
      frozenOffsetRef.current = null;
    }

    // Update prev progress tracker
    prevProgressRef.current = rotationProgress;
  }

  // Calculate rotation based on progress (rockProgress in MainPage)
  // LANDING 1 "something X" (progress = 0): Front-facing + cursor offset
  // LANDING 2 "meet the stone" (progress = 1): Tilted view
  // DEMO "hi, how can I help" (progress = 1, but controlled by frame3Progress): Front-facing
  const baseAngle = Math.PI / 4; // 45 degrees
  const frontFacingX = (Math.PI / 2) + (10 * Math.PI / 180); // 90° + 10° = 100° (front-facing)
  const frontFacingY = 0; // No horizontal rotation
  const frontFacingZ = 0; // No roll
  const tiltedX = baseAngle + (10 * Math.PI / 180); // 45° + 10° = 55° (angled forward tilt)
  const tiltedY = -baseAngle + (10 * Math.PI / 180); // -45° + 10° = -35° (turned to the right)
  const tiltedZ = baseAngle - (10 * Math.PI / 180); // 45° - 10° = 35° (some roll)

  // Interpolate: LANDING 1 (front-facing + cursor) → LANDING 2 (tilted)
  const landing2X = (frontFacingX + cursorOffsetX) * (1 - rotationProgress) + tiltedX * rotationProgress;
  const landing2Y = (frontFacingY + cursorOffsetY) * (1 - rotationProgress) + tiltedY * rotationProgress;
  const landing2Z = (frontFacingZ + cursorOffsetZ) * (1 - rotationProgress) + tiltedZ * rotationProgress;

  // Then interpolate: LANDING 2 (tilted) → DEMO (front-facing) using demoProgress
  const rotationX = landing2X * (1 - demoProgress) + frontFacingX * demoProgress;
  const rotationY = landing2Y * (1 - demoProgress) + frontFacingY * demoProgress;
  const rotationZ = landing2Z * (1 - demoProgress) + frontFacingZ * demoProgress;

  return (
    <primitive
      object={gltf.scene}
      scale={0.375 * scaleMultiplier}
      rotation={[rotationX, rotationY, rotationZ]}
    />
  );
}

interface StonePhone3DProps {
  className?: string;
  rotationProgress: number;
  scaleMultiplier?: number;
  opacity?: number;
  cursorX?: number;
  cursorY?: number;
  cursorRotationSettings?: {
    enabled: boolean;
    sensitivity: number;
    maxRotation: number;
  };
  isMobile?: boolean;
  autoRotationAngle?: number;
  noShine?: boolean;
  demoProgress?: number;
}

const StonePhone3D: React.FC<StonePhone3DProps> = ({
  className,
  rotationProgress,
  scaleMultiplier = 1,
  opacity = 1,
  cursorX = 0.5,
  cursorY = 0.5,
  cursorRotationSettings,
  isMobile = false,
  autoRotationAngle = 0,
  noShine = false,
  demoProgress = 0
}) => {
  const [lightPosition, setLightPosition] = useState({ x: 5, y: 16 });

  useEffect(() => {
    console.log('🎨 StonePhone3D component mounted');
  }, []);

  // Track cursor movement and update light position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Normalize cursor position to range -4 to 4 for slightly more movement
      const offsetX = (e.clientX / window.innerWidth - 0.5) * 8;
      const offsetY = -(e.clientY / window.innerHeight - 0.5) * 8; // Invert Y so up is positive

      // Base position: angled from top-right-front
      // Add subtle offsets based on cursor, keeping light angled up and away from camera
      const x = 5 + offsetX;
      const y = 16 + offsetY; // Always above center (16 base + offset) - moved up from 12

      setLightPosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className={className} style={{ width: '100%', height: '100%', pointerEvents: 'none', opacity }}>
      <Canvas
        camera={{ position: [0, 0, 12], fov: 50 }}
        style={{ background: 'transparent' }}
        gl={{
          alpha: true,
          antialias: true,
          powerPreference: 'high-performance',
          precision: 'highp',
          toneMapping: THREE.ACESFilmicToneMapping,
          outputColorSpace: THREE.SRGBColorSpace
        }}
        dpr={[2, 3]}
        shadows
        onCreated={({ gl }) => {
          console.log('🎨 Canvas created');
          // Force enable antialiasing and smooth rendering
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
        }}
      >
        {/* Environment map for smooth, realistic reflections on metallic surface */}
        <Environment preset="city" />

        {/* Soft ambient light base */}
        <ambientLight intensity={noShine ? 1.0 : 0.3} />

        {/* Directional light for noShine mode to show form */}
        {noShine && (
          <>
            <directionalLight position={[5, 5, 5]} intensity={1.5} />
            <directionalLight position={[-5, 3, 2]} intensity={1.0} />
          </>
        )}

        <Suspense fallback={<TestCube />}>
          <PhoneModel
            modelPath="/assets/3d/Stone_Visual_03_GLB_Export_01.glb"
            rotationProgress={rotationProgress}
            scaleMultiplier={scaleMultiplier}
            cursorX={cursorX}
            cursorY={cursorY}
            cursorRotationSettings={cursorRotationSettings}
            isMobile={isMobile}
            autoRotationAngle={autoRotationAngle}
            noShine={noShine}
            demoProgress={demoProgress}
          />
        </Suspense>
      </Canvas>
    </div>
  );
};

// Test cube to verify Canvas is rendering
function TestCube() {
  useEffect(() => {
    console.log('⚠️ Showing test cube fallback');
  }, []);

  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="white" />
    </mesh>
  );
}

export default React.memo(StonePhone3D);
